import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';

import { TReviewSource } from '@src/types/course';
import { formatToDate } from '@src/utils/time';

import Rating from '@src/components/common/rating';
import { BlankUserIcon } from '@src/components/common/icon';

const rateStyle = {
	fontSize: 20,
	marginLeft: 8,
	whiteSpace: 'nowrap',
};

const ReviewItemWrapper = styled.div`
	border-radius: 8px;
	padding: 8px 12px;
	background: #f9f9f9;
`;

const ReviewBlock = styled.div`
	padding: 12px 8px;
	border-top: 1px solid #ffffff;
	border-bottom: 1px solid #ffffff;
`;

const ReviewInfo = styled.div`
	display: flex;
`;

const User = styled.div`
	width: calc(100% - 48px);
	display: flex;
	align-items: center;
	justify-content: space-between;

	@media
		${props => props.theme.device.desktop},
		${props => props.theme.device.tablet},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile}
	{
		flex-direction: column;
		align-items: flex-start;
	}
`;

const AvatarWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: #ffffff;
`;

const AvatarContent = styled.div`
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background-image: url(${props => props.image});
	background-position: center top;
	background-size: cover;
`;

const BlankAvatar = styled(BlankUserIcon)`
	width: 44px;
	height: 44px;
`;

const UserInfo = styled.div`
	width: calc(100% - 168px); // Minus Rate width and margin
	margin: 0 8px;

	@media
		${props => props.theme.device.desktop},
		${props => props.theme.device.tablet},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile}
	{
		width: 100%;
	}
`;

const UserName = styled.div`
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: #101010;
`;

const UpdateTime = styled.div`
	font-size: 14px;
`;

const ReviewMessage = styled.pre`
	margin-top: 12px;
	font-family: 'Roboto', 'Noto Sans TC', '微軟正黑體', sans-serif;
	white-space: pre-wrap;
	word-break: break-word;
	color: #101010;
	${props => (props.showPartial ? `
		max-height: 72px;
		display: -webkit-box;
		text-overflow: ellipsis;
		overflow: hidden;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
	` : '')};

	@media ${props => props.theme.device.mobile} {
		font-size: 14px;
	}
`;

interface IReviewItem {
	className?: string;
	avatar?: string;
	userName: string;
	updatedTime: number;
	rating: number;
	message: string;
	source?: TReviewSource;
	originalClassName?: string;
	showPartial?: boolean;
}

const ReviewItem = ({
	className, avatar, userName, updatedTime, rating, message, source, originalClassName, showPartial = false,
}: IReviewItem): JSX.Element => {
	const { t } = useTranslation();

	return (
		<ReviewItemWrapper className={className}>
			<ReviewBlock>
				<ReviewInfo>
					<AvatarWrapper>
						{avatar ? <AvatarContent image={avatar} /> : <BlankAvatar />}
					</AvatarWrapper>
					<User>
						<UserInfo>
							<UserName>{userName}</UserName>
							<UpdateTime>
								{source ?
									`${originalClassName} - ${t(`course:propaganda_review_source:${source}`)}` :
									formatToDate(updatedTime)}
							</UpdateTime>
						</UserInfo>
						<Rating value={rating} style={rateStyle} />
					</User>
				</ReviewInfo>
				<ReviewMessage showPartial={showPartial}>{message}</ReviewMessage>
			</ReviewBlock>
		</ReviewItemWrapper>
	);
};

export default ReviewItem;
